import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openSetupJourneyOverlay } from "redux/redux";
import { PTFW_SOURCES } from "views/SetupJourney/constants";

/*
 * CAT-407 — the call sites' half of the overlay.
 *
 * Everything that used to `navigate(`${PAGE_URLS.SETUP_JOURNEY}?source=…`)`
 * now calls `open(source)` instead. Keeping that behind a hook rather than
 * letting four call sites each reach for `useDispatch` means the action's
 * payload shape is written down once, and it is the seam a test can assert on.
 */

/**
 * @param {string} [source] - a PTFW_SOURCES value; what `?source=` used to carry
 * @param {string} [hostPath] - pass the route being navigated to when the call
 *   site redirects as it opens, so the overlay anchors its history entry there
 *   and not to the screen the creator is leaving
 * @returns {(source?: string, hostPath?: string) => void}
 */
export const useOpenSetupJourneyOverlay = () => {
  const dispatch = useDispatch();

  return useCallback(
    (source = PTFW_SOURCES.HOME, hostPath = null) => {
      dispatch(openSetupJourneyOverlay({ source, hostPath }));
    },
    [dispatch]
  );
};

/**
 * The slide flag — true from the moment a call site opens the overlay. Drives
 * both halves of the motion: the journey's travel and Home's push.
 */
export const useIsSetupJourneyOverlayOpen = () =>
  useSelector((state) => !!state.setupJourneyOverlayReducer?.isOpen);

/**
 * True for as long as the journey is occupying the screen, which is one screen
 * duration longer than `isOpen` — the exit has to finish before anything
 * underneath is allowed to assume the journey is gone.
 */
export const useIsSetupJourneyOverlayMounted = () =>
  useSelector((state) => !!state.setupJourneyOverlayReducer?.isMounted);
